import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Hooks and Services
import { getMemberInfo } from '@/hooks/useMembers';
import { useVaccinations } from '@/hooks/useVaccinations';

// Types
import { IVaccination } from '@/types/vaccination';

// Custom Components
import AddVaccine from './AddVaccinePage';
import FormHeader from '@/components/ui/organisms/MHeader';
import VaccinationTabs from './VaccinationTabs';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import MLoader from '@/components/ui/atoms/MLoader';
import { appendToPath } from '@/utils/nav-utils';
import { MDialog } from '@/components/ui/atoms/MDialog';
import { getLearnMoreContent } from '@/utils/vaccination-utils';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const VaccinationTracker = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { derivedMemberType, onBehalf } = memberInfo;

  const {
    vaccinations,
    fetchVaccinations,
    deleteVaccination,
    markAsDone,
    loading
  } = useVaccinations(memberId);

  const [doVaccinesExist, setDoVaccinesExist] = useState(false);
  const [showAddVaccine, setShowAddVaccine] = useState(false);
  const [vaccinationToDelete, setVaccinationToDelete] = useState<IVaccination | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_VACCINATION,
    });
  }, []);

  // Set vaccinations from profile when loaded
  useEffect(() => {
    fetchVaccinations();
  }, [memberId, fetchVaccinations]);

  useEffect(() => {
    if (vaccinations && vaccinations.length === 0) {
      setDoVaccinesExist(false);
    } else {
      setDoVaccinesExist(true);
    }
  }, [vaccinations]);

  const handleAddVaccination = () => {
    // Track add vaccination
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_VACCINATION,
    });
    
    if (derivedMemberType === 'C') {
      setShowAddVaccine(true);
    } else {
      navigate(appendToPath(location.pathname, 'add-vaccine'));
    }
  };

  const handleEditVaccination = (vaccination: IVaccination) => {
    navigate(appendToPath(location.pathname, 'add-vaccine'), { state: { vaccination } });
  };

  const handleMarkAsDone = async (vaccination: IVaccination) => {
    try {
      await markAsDone(vaccination.uid);
      // Explicitly fetch vaccinations again to ensure UI updates
      await fetchVaccinations();

      // Update the URL to show the 'taken' tab
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', 'taken');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

      showToast({
        message: `${t('vaccinations.markAsDone.success', { name: vaccination.name })}`,
        variant: 'success',
      });
    } catch (error) {
      showToast({
        message: `${t('errors.toast')}`,
        variant: 'error',
      });
    }
  };

  const handleDeleteClick = (vaccination: IVaccination) => {
    setVaccinationToDelete(vaccination);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!vaccinationToDelete?.uid) return;

    try {
      await deleteVaccination(vaccinationToDelete.uid);
      // Update the URL to show the 'taken' tab
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', vaccinationToDelete.taken ? 'taken' : 'pending');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    } catch (error) {
      showToast({
        message: `${t('errors.toast')}`,
        variant: 'error',
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setVaccinationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setVaccinationToDelete(null);
  };

  if (loading) {
    return (
      <MLoader />
    );
  }

  return (
    <div className="profile-page-root">
      {/* Form Header */}
      <FormHeader
        module="vaccinations"
        memberInfo={memberInfo}
        onAdd={handleAddVaccination}
        showGradient={true}
        learnMoreContent={getLearnMoreContent(derivedMemberType, t)}
      />

      <>
        {!doVaccinesExist || showAddVaccine ? (
          <AddVaccine doVaccinesExist={doVaccinesExist} />
        ) : (
          vaccinations && vaccinations.length > 0 && (
            <VaccinationTabs
              vaccinations={vaccinations}
              onEdit={handleEditVaccination}
              onMarkAsDone={handleMarkAsDone}
              onDelete={handleDeleteClick}
            />
          )
        )}
      </>

      <MDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        title={t('vaccinations.deleteConfirmation.title')}
        description={onBehalf ? t('vaccinations.deleteConfirmation.descriptionAO') : t('vaccinations.deleteConfirmation.description')}
        primaryCtaText={t('common.yes')}
        onPrimaryCta={handleConfirmDelete}
        secondaryCtaText={t('common.no')}
        onSecondaryCta={handleCancelDelete}
      />
    </div>
  );
};

export default VaccinationTracker;
